import subscriptionService from '@/modules/subscription/services/subscriptionService';
import licenseService from '@/modules/subscription/services/licenseService';
import currencyUtil from '@/modules/common/utils/currencyUtil';
import logError from '@/utils/logError/logError';

const DEFAULT_PAYMENT_TYPE = 'monthly';

const state = {
  subscription: undefined,
  activeSubscriptions: [],
  purchasableApps: {},
  featurePackages: [],
  currentBusinessPackage: null,
};

const mutations = {
  setSubscription(state, value) {
    state.subscription = value;
  },
  setActiveSubscriptions(state, subscriptions) {
    state.activeSubscriptions = subscriptions;
  },
  setPurchasableApps(state, apps) {
    state.purchasableApps = apps;
  },
  setFeaturePackages(state, packages) {
    state.featurePackages = packages;
  },
  setCurrentBusinessPackage(state, businessPackage) {
    state.currentBusinessPackage = businessPackage;
  },
};

const findUserPrice = (geoData, app) => {
  const accountCurrency = currencyUtil.getCurrency(geoData.country, geoData.continent);
  return app.prices?.find((item) => item.currency === accountCurrency);
};

const purchasedAppData = (subscription, priceInfo) => {
  const payPerUnit = priceInfo.pay_per_unit || [];
  const paymentType = subscription.payment_type || DEFAULT_PAYMENT_TYPE;

  return {
    base_price: priceInfo.price,
    subscription_uid: subscription.uid,
    price: subscription.purchase_price,
    currency: subscription.purchase_currency,
    payment_type: paymentType,
    purchase_state: subscription.purchase_state,
    subscription_created_at: subscription.created_at,
    price_uid: priceInfo.price_uid || priceInfo.uid,
    pay_per_unit: payPerUnit,
    requires_purchase: false,
    is_included: paymentType === 'bundle',
  };
};

const paidAppData = (priceInfo) => {
  const payPerUnit = priceInfo.pay_per_unit || [];

  return {
    base_price: priceInfo.price,
    price: priceInfo.price,
    currency: priceInfo.currency,
    payment_type: priceInfo.payment_type,
    purchase_state: 'not_purchased',
    price_uid: priceInfo.uid,
    pay_per_unit: payPerUnit,
    requires_purchase: true,
    is_included: priceInfo.payment_type === 'bundle',
  };
};

const actions = {
  async fetchSubscription({ commit, state }) {
    if (!state.subscription) {
      const subscriptionValue = await subscriptionService.getSubscriptionDetails();

      if (!subscriptionValue) throw new Error('Failed to fetch subscription');

      commit('setSubscription', subscriptionValue);
    }
  },
  async fetchSubscriptionByUid({ commit }, subscriptionUid) {
    const subscriptionValue = await subscriptionService.getSubscriptionByUid(subscriptionUid);

    if (!subscriptionValue) throw new Error('Failed to fetch subscription');

    commit('setSubscription', subscriptionValue);
  },
  async fetchBundledSubscriptions(_, parentSubscriptionUid) {
    return subscriptionService.getBundledSubscriptions(parentSubscriptionUid);
  },
  async fetchActiveSubscriptions({ commit, state }) {
    if (!state.activeSubscriptions.length) {
      const activeSubscriptions = await subscriptionService.getActiveSubscriptions();

      if (!activeSubscriptions) throw new Error('Failed to fetch active subscriptions');

      commit('setActiveSubscriptions', activeSubscriptions);
    }
  },
  async fetchPurchasableApps({ commit, state }, appCodeNames) {
    const cacheKey = appCodeNames.sort().join(',');

    if (!state.purchasableApps[cacheKey]) {
      const purchasableApps = await subscriptionService.getPurchasableApps(appCodeNames);

      if (!purchasableApps) throw new Error('Failed to fetch purchasable apps');

      commit('setPurchasableApps', { ...state.purchasableApps, [cacheKey]: purchasableApps });
    }

    return state.purchasableApps[cacheKey];
  },
  async getPaidAppsData({ dispatch, getters }, { apps, geoData }) {
    await dispatch('fetchActiveSubscriptions');
    const purchasableApps = await dispatch('fetchPurchasableApps', apps.map((app) => app.app_code_name));

    const paidAppsUids = Object.keys(purchasableApps);
    const result = {};

    paidAppsUids.forEach((paidAppUid) => {
      const appCodeName = purchasableApps[paidAppUid].item_uid;
      const priceInfo = findUserPrice(geoData, purchasableApps[paidAppUid]);

      const subscription = getters.activeSubscriptions.find((sub) => sub.sku === appCodeName);

      if (subscription && priceInfo) {
        result[appCodeName] = purchasedAppData(subscription, priceInfo);
      } else if (priceInfo) {
        result[appCodeName] = paidAppData(priceInfo);
      } else {
        const appIndex = apps.findIndex((app) => app.app_code_name === appCodeName);
        if (appIndex !== -1) {
          apps.splice(appIndex, 1);
        }
      }
    });

    return result;
  },
  async aggregateAppData({ dispatch }, { apps, geoData }) {
    const paidAppsData = await dispatch('getPaidAppsData', { apps, geoData });

    apps.forEach((app) => {
      if (paidAppsData[app.app_code_name]) {
        const updatedApp = { ...app };
        updatedApp.paid_data = paidAppsData[app.app_code_name];
        updatedApp.requires_upgrade = app.requires_upgrade || app.requires_upgrade_if_paid;
        Object.assign(app, updatedApp);
      }
    });

    return apps;
  },
  async purchaseSubscription({ commit }, params) {
    const res = await subscriptionService.createSubscription(params);

    if (!res) throw new Error('Failed to purchase subscription');

    commit('setSubscription', res);
  },
  async createV3Subscription({ commit }, params) {
    const res = await subscriptionService.createV3Subscription(params);

    if (!res) throw new Error('Failed to purchase subscription');

    commit('setSubscription', res);
  },
  async cancelSubscription({ getters }) {
    await subscriptionService.cancelSubscription(getters.subscription.uid);
  },
  async pollingSubscriptionStatus({ getters, dispatch }, subscriptionUid) {
    return new Promise((resolve) => {
      let requestInterval;

      const makeRequest = async () => {
        try {
          const purchaseState = getters.subscription.purchase_state;

          if (purchaseState === 'purchased') {
            clearInterval(requestInterval);
            resolve('success');
          } else if (purchaseState === 'canceled' || purchaseState === 'expired') {
            clearInterval(requestInterval);
            resolve('error');
          } else {
            await dispatch('fetchSubscriptionByUid', subscriptionUid);
          }
        } catch (error) {
          logError('Failed to fetch subscription during polling', error, { subscriptionUid });
          clearInterval(requestInterval);
          resolve('error');
        }
      };

      setTimeout(() => {
        clearInterval(requestInterval);
        resolve('error');
      }, 17500);

      requestInterval = setInterval(makeRequest, 1000);
    });
  },
  async fetchFeaturePackages({ commit, state }) {
    if (!state.featurePackages.length) {
      try {
        const featurePackages = await licenseService.getAllFeaturePackages();
        commit('setFeaturePackages', featurePackages);
      } catch (error) {
        logError('Failed to fetch feature packages', error);
        throw new Error('Failed to fetch feature packages');
      }
    }
  },
  async loadCurrentBusinessPackage({ commit, dispatch, state }) {
    try {
      // Use the main subscription from state
      const mainSubscription = state.subscription;

      if (mainSubscription?.purchasable_item_uid) {
        // Ensure feature packages are loaded
        await dispatch('fetchFeaturePackages');

        // Find the current business package by SKU
        const featurePackages = state.featurePackages;
        const currentPackage = licenseService.findFeaturePackageByName(
          featurePackages,
          mainSubscription.purchasable_item_uid,
        );

        commit('setCurrentBusinessPackage', currentPackage);
      }
    } catch (error) {
      logError('Error loading current business package', error);
    }
  },
};

const getters = {
  subscription: (state) => state.subscription,
  activeSubscriptions: (state) => state.activeSubscriptions,
  purchasableApps: (state) => state.purchasableApps,
  getExtras: (state) => state.activeSubscriptions.filter((subscription) => (subscription.offering_type === 'addon' || subscription.offering_type === 'one_time_purchase')),
  featurePackages: (state) => state.featurePackages,
  currentBusinessPackage: (state) => state.currentBusinessPackage,
  isSmsPurchaseDisabled: (state) => state.currentBusinessPackage?.settings?.disable_sms_purchase_button === true,
  hasActiveSubscriptionToApp: () => (appCodeName) => subscriptionService.hasActiveSubscriptionToApp(appCodeName),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
